import { useCallback } from 'react';
import { useSnackbarStore } from '../../../shared/stores/useSnackbarStore';
import { useRevitApi } from '../../../shared/api/revit/useRevitApi';
import {
  buildScopedReport,
  createHtmlFromReport,
  type ValidationExportScope,
} from '../lib/validationReportNormalizer';
import { buildExportFileName, downloadFile } from '../lib/validationStateHelpers';
import { USE_GRPC, USE_MOCK } from '../lib/validationRuntimeEnv';
import type { PcfValidationReport, ValidationCheckResult } from '../types';

export interface UseValidationExportOptions {
  report: PcfValidationReport | null;
  documentInfo: any;
  /**
   * Whether the visible report still describes the current context. A report
   * captured before a setup revision, connector mutation, profile change or
   * model change is evidence about a model that no longer exists, so it must
   * not leave the app as a document. Defaults to `true` for callers that have
   * no context tracking yet.
   */
  isReportCurrent?: boolean;
}

const StaleReportMessage =
  'These validation results are out of date. Run validation again before exporting.';
const MissingReportMessage = 'Run validation first to export results';
const MissingCheckMessage = 'Select a validation check to export current result';

export interface UseValidationExportReturn {
  handleExportJson: (
    scope?: ValidationExportScope,
    validationId?: number,
    selectedCheck?: ValidationCheckResult | null
  ) => Promise<void>;
  handleExportHtml: (
    scope?: ValidationExportScope,
    validationId?: number,
    selectedCheck?: ValidationCheckResult | null
  ) => Promise<void>;
}

export function useValidationExport(options: UseValidationExportOptions): UseValidationExportReturn {
  const { report, documentInfo, isReportCurrent = true } = options;
  const snackbar = useSnackbarStore();
  const { call } = useRevitApi();

  /** Names the exact report slice this export may write, or refuses and says why. */
  const resolveExportableReport = useCallback(
    (
      scope: ValidationExportScope,
      validationId?: number,
      selectedCheckOverride?: ValidationCheckResult | null
    ) => {
      if (!report) {
        snackbar.showError(MissingReportMessage);
        return null;
      }

      if (!isReportCurrent) {
        snackbar.showError(StaleReportMessage);
        return null;
      }

      const scoped = buildScopedReport(report, scope, validationId, selectedCheckOverride);
      if (!scoped) {
        snackbar.showError(MissingCheckMessage);
        return null;
      }

      return scoped;
    },
    [report, isReportCurrent, snackbar]
  );

  const handleExportJson = useCallback(
    async (
      scope: ValidationExportScope = 'full',
      validationId?: number,
      selectedCheckOverride?: ValidationCheckResult | null
    ) => {
      const scoped = resolveExportableReport(scope, validationId, selectedCheckOverride);
      if (!scoped) {
        return;
      }

      const fileName = buildExportFileName(
        documentInfo?.DocumentName,
        'json',
        scope,
        scoped.check
      );

      if (scope === 'current') {
        downloadFile(JSON.stringify(scoped.report, null, 2), fileName, 'application/json');
        snackbar.showSuccess(
          `Validation #${scoped.check?.ValidationId ?? validationId} exported as JSON`
        );
        return;
      }

      if (USE_MOCK || USE_GRPC) {
        downloadFile(JSON.stringify(scoped.report, null, 2), fileName, 'application/json');
        snackbar.showSuccess('Full report exported as JSON');
        return;
      }

      try {
        const result = await call('validation.exportToJson');
        if (result.success && result.json) {
          downloadFile(String(result.json), fileName, 'application/json');
          snackbar.showSuccess('Full report exported as JSON');
          return;
        }

        downloadFile(JSON.stringify(scoped.report, null, 2), fileName, 'application/json');
        snackbar.showSuccess('Full report exported as JSON');
      } catch (err: any) {
        downloadFile(JSON.stringify(scoped.report, null, 2), fileName, 'application/json');
        snackbar.showSuccess('Full report exported as JSON');
      }
    },
    [resolveExportableReport, call, documentInfo, snackbar]
  );

  const handleExportHtml = useCallback(
    async (
      scope: ValidationExportScope = 'full',
      validationId?: number,
      selectedCheckOverride?: ValidationCheckResult | null
    ) => {
      const scoped = resolveExportableReport(scope, validationId, selectedCheckOverride);
      if (!scoped) {
        return;
      }

      const fileName = buildExportFileName(
        documentInfo?.DocumentName,
        'html',
        scope,
        scoped.check
      );

      if (scope === 'current') {
        downloadFile(createHtmlFromReport(scoped.report), fileName, 'text/html');
        snackbar.showSuccess(
          `Validation #${scoped.check?.ValidationId ?? validationId} exported as HTML`
        );
        return;
      }

      if (USE_MOCK || USE_GRPC) {
        downloadFile(createHtmlFromReport(scoped.report), fileName, 'text/html');
        snackbar.showSuccess('Full report exported as HTML');
        return;
      }

      try {
        const result = await call('validation.exportToHtml');
        if (result.success && result.html) {
          downloadFile(String(result.html), fileName, 'text/html');
          snackbar.showSuccess('Full report exported as HTML');
          return;
        }

        downloadFile(createHtmlFromReport(scoped.report), fileName, 'text/html');
        snackbar.showSuccess('Full report exported as HTML');
      } catch (err: any) {
        downloadFile(createHtmlFromReport(scoped.report), fileName, 'text/html');
        snackbar.showSuccess('Full report exported as HTML');
      }
    },
    [resolveExportableReport, call, documentInfo, snackbar]
  );

  return {
    handleExportJson,
    handleExportHtml,
  };
}
